import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { API } from '../../lib/api';

const POSITIONS = ['Pitcher', 'Catcher', '1st Base', '2nd Base', '3rd Base', 'Shortstop', 'Outfield', 'Utility'];
const LEAGUE_LEVELS = ['14u', '16u', '18u'];
const BATTING_SKILLS = ['Switch hit', 'Bats left', 'Slap hits'];

export const TryoutRegistrationForm = () => {
  const { toast } = useToast();
  const initialState = useMemo(() => ({
    parentName: '',
    parentContactNumber: '',
    playerName: '',
    playerContactNumber: '',
    playerAge: '',
    preferredPosition: '',
    otherPositions: [],
    currentTeam: '',
    currentLeagueLevel: '',
    battingSkills: [],
    commitmentAcknowledged: false,
  }), []);
  const [formData, setFormData] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field, value) => setFormData((current) => ({ ...current, [field]: value }));

  const toggleArrayValue = (field, value, limit = null) => {
    setFormData((current) => {
      const exists = current[field].includes(value);
      const nextValues = exists ? current[field].filter((item) => item !== value) : [...current[field], value];
      if (!exists && limit && nextValues.length > limit) {
        return current;
      }
      return { ...current, [field]: nextValues };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/tryout-registrations`, {
        ...formData,
        playerAge: Number(formData.playerAge),
      });
      toast({
        title: 'Pre-registration submitted',
        description: 'Thank you. We will review your tryout registration and follow up soon.',
      });
      setFormData(initialState);
    } catch (error) {
      toast({
        title: 'Submission failed',
        description: 'There was a problem submitting this tryout registration. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-0 shadow-2xl bg-white" data-testid="softball-tryout-form-card">
      <CardHeader>
        <CardTitle className="text-3xl" style={{ color: '#7e2f22' }}>Pre-register for Tryouts</CardTitle>
        <CardDescription>Share player details now so the coaching team can prepare for tryout day.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-6" onSubmit={handleSubmit} data-testid="softball-tryout-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="parentName">Parent / Guardian Name</Label>
              <Input id="parentName" value={formData.parentName} onChange={(event) => updateField('parentName', event.target.value)} required data-testid="tryout-parent-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parentContactNumber">Parent Contact Number</Label>
              <Input id="parentContactNumber" value={formData.parentContactNumber} onChange={(event) => updateField('parentContactNumber', event.target.value)} required data-testid="tryout-parent-contact-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="playerName">Player Name</Label>
              <Input id="playerName" value={formData.playerName} onChange={(event) => updateField('playerName', event.target.value)} required data-testid="tryout-player-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="playerContactNumber">Player Contact Number</Label>
              <Input id="playerContactNumber" value={formData.playerContactNumber} onChange={(event) => updateField('playerContactNumber', event.target.value)} required data-testid="tryout-player-contact-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="playerAge">Player Age</Label>
              <Input id="playerAge" type="number" min="10" max="18" value={formData.playerAge} onChange={(event) => updateField('playerAge', event.target.value)} required data-testid="tryout-player-age-input" />
            </div>
            <div className="space-y-2">
              <Label>Current League Level</Label>
              <Select onValueChange={(value) => updateField('currentLeagueLevel', value)} value={formData.currentLeagueLevel}>
                <SelectTrigger data-testid="tryout-league-level-select">
                  <SelectValue placeholder="Select league level" />
                </SelectTrigger>
                <SelectContent>
                  {LEAGUE_LEVELS.map((level) => <SelectItem key={level} value={level}>{level}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Preferred Position</Label>
              <Select onValueChange={(value) => updateField('preferredPosition', value)} value={formData.preferredPosition}>
                <SelectTrigger data-testid="tryout-preferred-position-select">
                  <SelectValue placeholder="Select the main position" />
                </SelectTrigger>
                <SelectContent>
                  {POSITIONS.map((position) => <SelectItem key={position} value={position}>{position}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <Label>Top 2 Other Positions</Label>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="tryout-other-positions-group">
              {POSITIONS.filter((position) => position !== formData.preferredPosition).map((position) => (
                <label key={position} className="flex items-center gap-3 rounded-xl border p-3 text-sm">
                  <Checkbox checked={formData.otherPositions.includes(position)} onCheckedChange={() => toggleArrayValue('otherPositions', position, 2)} data-testid={`tryout-other-position-${position.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
                  <span>{position}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="currentTeam">Current Team</Label>
            <Input id="currentTeam" value={formData.currentTeam} onChange={(event) => updateField('currentTeam', event.target.value)} data-testid="tryout-current-team-input" />
          </div>

          <div className="space-y-3">
            <Label>Batting Skills</Label>
            <div className="grid sm:grid-cols-3 gap-3" data-testid="tryout-batting-skills-group">
              {BATTING_SKILLS.map((skill) => (
                <label key={skill} className="flex items-center gap-3 rounded-xl border p-3 text-sm">
                  <Checkbox checked={formData.battingSkills.includes(skill)} onCheckedChange={() => toggleArrayValue('battingSkills', skill)} data-testid={`tryout-batting-skill-${skill.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
                  <span>{skill}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-[#dbbf57] bg-[#dbbf5715] p-4 space-y-3" data-testid="tryout-commitment-note">
            <p className="text-sm text-gray-700">Players must commit to practicing with the team and commit to quality performance on and off the field. Invitation to the Hawaii trip is based on these aspects. We will accommodate other team practices when discussed with coaches.</p>
            <label className="flex items-start gap-3 text-sm">
              <Checkbox checked={formData.commitmentAcknowledged} onCheckedChange={(checked) => updateField('commitmentAcknowledged', Boolean(checked))} data-testid="tryout-commitment-checkbox" />
              <span>I understand and agree to this commitment expectation.</span>
            </label>
          </div>

          <Button type="submit" disabled={submitting || !formData.commitmentAcknowledged} className="w-full text-white" style={{ backgroundColor: '#e37c55' }} data-testid="tryout-submit-button">
            {submitting ? 'Submitting…' : 'Submit Pre-registration'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};